import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Nav.css';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider, firestore } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FaUser, FaComments, FaHeart, FaFileAlt, FaTag, FaInfoCircle, FaDownload } from 'react-icons/fa';
import ReactGA from 'react-ga4';
import { toast } from 'react-toastify';

const Nav = ({ solid = false, guestLanding = false }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('userId'));
  const [scrollY, setScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [platform, setPlatform] = useState('Web');
  const [isInstalling, setIsInstalling] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  useEffect(() => {
    const detectPlatform = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      return isPWA ? 'PWA' : 'Web';
    };
    setPlatform(detectPlatform());
  }, []);

  const getPlatform = () => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      return 'PWA';
    }
    return 'Website';
  };

  // Function to track a button click event
  const trackEvent = (category, action, label = '') => {
    // Function is depricated
    // ReactGA.event({
    //   category,
    //   action: action + " - " + getPlatform(),
    //   label,
    //   platform: getPlatform(),
    // });
  };

  useEffect(() => {
    const checkPWAStatus = () => {
      const isRunningAsPWA = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone ||
        document.referrer.includes('android-app://');
      setPlatform(isRunningAsPWA ? 'PWA' : 'Web');
      return isRunningAsPWA;
    };

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
      localStorage.removeItem('installDismissed'); // Reset dismissal if prompt reappears
    };

    // Immediate check on component mount
    if (!checkPWAStatus()) {
      window.addEventListener('beforeinstallprompt', handler);
    }

    const installedHandler = () => {
      setDeferredPrompt(null);
      setShowInstallButton(false);
      localStorage.setItem('installDismissed', 'true');
    };

    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback for when prompt isn't immediately available
      alert('For the best experience, please use Chrome or Safari and look for "Add to Home Screen" in the browser menu.');
      return;
    }

    setIsInstalling(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      if (choice.outcome === 'accepted') {
        trackEvent(`Install Clicked`, `Install Clicked (Nav)`, 'App side menu');
        console.log('User installed the app');
      } else {
        console.log('User dismissed the install prompt');
      }
    } catch (error) {
      console.error('Installation failed:', error);
      alert('Installation failed. Please try again or use Chrome/Safari.');
    } finally {
      setDeferredPrompt(null);
      setShowInstallButton(false);
      setIsInstalling(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setIsLoggedIn(!!user || !!localStorage.getItem('userId'));
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userRef = doc(firestore, 'users', user.uid);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        });
        localStorage.setItem('userId', user.uid);
        toast.success('Logged in successfully!');
        navigate('/form?login=complete');
      } else {
        // User exists - check if they have complete profile details
        const userData = docSnap.data();
        console.log('User data from Firestore:', userData);
        console.log('Has dob:', !!userData.dob, 'Has time:', !!userData.time, 'Has location:', !!userData.location);
        localStorage.setItem('userId', user.uid);
        toast.success('Welcome back!');
        if (userData.dob && userData.time && userData.location) {
          window.location.href = '/chat?login=complete';
        } else {
          window.location.href = '/form?login=complete';
        }
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      toast.error('Error signing in with Google');
    } finally {
      setLoginLoading(false);
    }
  };

  const showProfile = isLoggedIn && !guestLanding;
  const isScrolled = solid || scrollY > 45 || showProfile;

  return (
    <div className={`nav-bar ${isScrolled ? 'nav-scrolled' : ''}`}>
      <Link to="/" className='gradient-text'>
        Vedic Rashi
      </Link>

      <div className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`} onClick={toggleMobileMenu}>
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div id='nav-right' className={isMobileMenuOpen ? 'open' : ''}>
        <div className="nav-group">
          <Link to="/chat" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaComments /></span> Chat
          </Link>
          <Link to="/kundli-analysis" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaHeart /></span> Kundli Matching
          </Link>
          <Link to="/mini-reports" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaFileAlt /></span> Reports
          </Link>
        </div>

        <div className="nav-group">
          <Link to="/pricing" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaTag /></span> Pricing
          </Link>
          <Link to="/about" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon"><FaInfoCircle /></span> About
          </Link>
        </div>


        {showProfile ? (
          <Link to="/profile" className="nav-cta" onClick={() => setIsMobileMenuOpen(false)}>
            <FaUser size={13} /> My Profile
          </Link>
        ) : (
          <button
            className="nav-cta"
            onClick={() => { handleGoogleLogin(); setIsMobileMenuOpen(false); }}
            disabled={loginLoading}
          >
            {loginLoading ? 'Signing in...' : 'Get Started →'}
          </button>
        )}
      </div>
    </div>
  );
};

export default Nav;
